"use client";
import React, { useContext } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Typography } from "@/components/Typography";
import { featuredProps } from "@/data/types/home";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { paths } from "@/utils/paths";
import { BannerWrapperSize } from "@/components/Banner/types";
import { Banner } from "@/components/Banner";

import "./styles.scss";

// `Featured` is server-rendered: its former `ssr: false` worked around a
// hydration mismatch caused by `useWindowSize` reading `window.innerWidth`
// during the first client render. That hook is now hydration-safe, so the
// steps render in the server HTML and no longer pop in (CLS).
const Featured = dynamic(() => import("@/components/Featured").then((mod) => mod.Featured));

export const ThreeStepsModule = ({
  data,
  wrapperSize,
  variant,
}: {
  data: featuredProps;
  wrapperSize?: BannerWrapperSize;
  /**
   * `home` renders the restored Home variant (`/`): `big` Banner on the
   * content's background, no subtitle, and the local square step visuals.
   * Omitted renders the redesigned variant used by `/qr-code-generator`
   * (`neutral-50`, `withoutPadding`, subtitle and numbered cards).
   * See docs/HOME.md.
   */
  variant?: "home";
}) => {
  const router = useRouter();
  const { List, Title, Subtitle, ctaLabel, data_qa, customClass, backgroundColor } = data;
  const { setSource } = useContext(AmplitudeContext);

  const isHome = variant === "home";

  const handleStepsClick = () => {
    setSource("cta_steps");
    router.push(paths.qrEditor());
  };

  return (
    <Banner
      medium={isHome}
      light
      withoutPadding={!isHome}
      customBgColor={isHome ? backgroundColor : "neutral-50"}
      wrapperSize={wrapperSize}
      data-testid="three-steps-module"
      customClass={isHome ? "three-steps-module-banner-home" : undefined}
    >
      <div
        className={isHome ? "three-steps-module three-steps-module--home" : "three-steps-module"}
      >
        <Typography
          Tag={"h2"}
          spacing={8}
        >
          {Title}
        </Typography>

        {!isHome && (
          <Typography
            Tag={"p"}
            spacing={8}
          >
            {Subtitle}
          </Typography>
        )}

        <Featured
          items={List}
          customClass={customClass}
          variant={isHome ? "home" : undefined}
        />
        <div className="buttonWrapper cta-steps-banner-btn">
          <ButtonLib
            dataQA={data_qa}
            onClick={handleStepsClick}
            label={ctaLabel}
            variant="secondary"
          />
        </div>
      </div>
    </Banner>
  );
};
